"use client";

import React from "react";
import { motion } from "framer-motion";
import { Sparkles, ShieldCheck, Clock3, Users, MapPin, CircleCheck as CheckCircle2, Phone, MessageCircle, Flame, Wrench, SearchCheck, Check } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PHONE_LOCAL, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
const cleaningPhases = [{
  icon: Wrench,
  title: "فك الرفوف والشوايات",
  description: "تفكيك القطع الداخلية والشبكات ونقعها بمواد متخصصة لإذابة الشحوم المتراكمة دون إتلاف طبقات الكروم."
}, {
  icon: Sparkles,
  title: "جلي وتلميع الزجاج",
  description: "تنظيف عميق لأبواب الزجاج المزدوجة وإزالة ضبابية الدهون والحروق القديمة لاستعادة وضوحه التام."
}, {
  icon: Flame,
  title: "إزالة ترسبات الكربون",
  description: "تطهير التجويف الداخلي للفرن ومعالجة البقع المتكلسة بمركبات آمنة تحمي طلاء المينا الأصلي للأفران."
}, {
  icon: SearchCheck,
  title: "فحص الشعلات والتوصيلات",
  description: "تسليك عيون الغاز وفحص الحساسات والدوائر الكهربائية لضمان كفاءة الاشتعال والسلامة بعد التنظيف."
}];
const whyChooseFeatures = [{
  icon: Clock3,
  title: "سرعة الوصول لحي النخيل",
  description: "فرق متنقلة تغطي حي النخيل بالرياض مع التزام تام بالمواعيد المحددة ومرونة تلائم أوقات العائلة."
}, {
  icon: ShieldCheck,
  title: "مواد آمنة ومعتمدة",
  description: "منظفات ومذيبات دهون أوروبية معتمدة لا تترك روائح ضارة ولا تؤثر على طلاء وهيكل الفرن الداخلي."
}, {
  icon: Users,
  title: "طاقم فني متخصص",
  description: "فنيون محترفون ذوو خبرة واسعة في التعامل مع مختلف العلامات التجارية للأفران الغازية والكهربائية."
}, {
  icon: CheckCircle2,
  title: "خدمة منزلية متكاملة",
  description: "تنفيذ العمل داخل مطبخك بأعلى معايير النظافة مع حماية الأسطح المحيطة ودون إحداث أي فوضى."
}];
const coverageAreas = ["حي النخيل الغربي والشرقي", "حي العقيق المجاور", "حي الملقا", "حي الياسمين", "حي حطين والشمال القريب"];
const faqs = [{
  question: "ما هو المعدل الموصى به لتنظيف الفرن منزلياً؟",
  answer: "يُفضل إجراء تنظيف عميق ومتخصص للفرن كل 3 إلى 6 أشهر حسب كثافة الاستخدام، مع التنظيف السطحي المنتظم بعد كل طهي ثقيل لتفادي تصلب الدهون وتحولها إلى ترسبات كربونية صعبة الإزالة."
}, {
  question: "كم تستغرق زيارة التنظيف المتخصصة داخل المنزل؟",
  answer: "تستغرق الزيارة المعتادة من ساعة إلى ساعتين، تبعاً لحجم الفرن ودرجة تراكم الشحوم. يقوم الفريق بإنجاز العمل كاملاً في نفس الزيارة مع إعادة تركيب كافة القطع واختبار كفاءة التشغيل فوراً."
}, {
  question: "هل مواد التنظيف آمنة على أفران الغاز والكهرباء والأسطح الحساسة؟",
  answer: "نعم، نستخدم حصرياً محاليل تنظيف مخصصة للأفران خالية من الأحماض الحارقة أو الكواشط القاسية، وهي آمنة تماماً على الستانلس ستيل، زجاج الأمان الحراري، طلاء المينا، والمكونات الحساسة."
}, {
  question: "كيف يمكنني حجز موعد تنظيف في حي النخيل؟",
  answer: `يمكنك حجز موعدك فوراً عبر التواصل المباشر عبر الواتساب أو الاتصال على الرقم ${PHONE_LOCAL}، حيث يتم تسجيل بياناتك وتحديد الوقت المناسب لزيارة فنيينا لك في حي النخيل.`
}];
export default function AlNakheelOvenCleaningService() {
  return <section id="alnakheelovencleaningservice" data-nav="light" dir="rtl" className="bg-background text-foreground py-16 sm:py-24 overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-16 sm:space-y-24">
        {/* Block 1: Header & Service Overview */}
        <div className="space-y-10">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          ease: "easeOut"
        }} className="text-center max-w-3xl mx-auto space-y-4">
            <div className="inline-flex items-center gap-2">
              <Badge variant="outline" className="px-3 py-1 font-medium border-border">
                حي النخيل • الرياض
              </Badge>
              <div className="w-8 h-[3px] bg-primary rounded-full" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
              خدمة تنظيف الأفران في حي النخيل بالرياض
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              تنظيف عميق وشامل لحجرات الأفران، الشوايات، صواني الحرق، والأبواب الزجاجية المزدوجة. نتخلص من أصعب الدهون وترسبات الكربون المستعصية باستخدام مواد متطورة تحافظ بدقة على طلاء المينا والمكونات الداخلية.
            </p>
          </motion.div>

          {/* 4 Cleaning Phases */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {cleaningPhases.map((phase, i) => {
            const Icon = phase.icon;
            return <motion.div key={phase.title} data-index={i} initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.45,
              delay: i * 0.08,
              ease: "easeOut"
            }}>
                  <Card className="h-full border border-border shadow-xs hover:shadow-md transition-shadow">
                    <CardHeader className="space-y-3 pb-3">
                      <div className="size-12 rounded-lg bg-accent flex items-center justify-center text-primary">
                        <Icon className="size-6" />
                      </div>
                      <CardTitle className="text-lg font-bold text-foreground">
                        {phase.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {phase.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Block 2: Why Choose لمسة احتراف */}
        <div className="space-y-10">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          ease: "easeOut"
        }} className="text-center max-w-2xl mx-auto space-y-3">
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
              لماذا تختار لمسة احتراف لتنظيف أفرانك؟
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base">
              نضمن لك أعلى درجات الدقة والمهنية مع خبرة عميقة تلبي توقعات سكان حي النخيل بالرياض.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseFeatures.map((feat, i) => {
            const Icon = feat.icon;
            return <motion.div key={feat.title} data-index={i} initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.45,
              delay: i * 0.08,
              ease: "easeOut"
            }} className="rounded-xl border border-border bg-card p-6 flex flex-col gap-4 shadow-2xs hover:border-primary/40 transition-colors">
                  <div className="size-12 rounded-full border border-border flex items-center justify-center text-primary bg-muted">
                    <Icon className="size-6" />
                  </div>
                  <h4 className="text-base font-bold text-foreground">
                    {feat.title}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {feat.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Block 3: Service Coverage & In-Home Visits */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        ease: "easeOut"
      }} className="rounded-2xl border border-border bg-muted/50 p-6 sm:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 text-primary font-medium text-sm">
                <MapPin className="size-4" />
                <span>نطاق التغطية والزيارات المنزلية</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
                خدمة منزلية متخصصة تغطي حي النخيل والمناطق المجاورة
              </h3>
              <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
                نصل إليكم بفرق متنقلة مجهزة بأحدث المعدات ومواد التنظيف الاحترافية داخل حي النخيل وأحياء شمال وغرب الرياض. نضمن إنجاز العمل بالكامل في موقعك دون نقل الفرن، مع توفير أقصى درجات النظافة وحماية المطابخ.
              </p>
            </div>

            <div className="lg:col-span-5 bg-card border border-border rounded-xl p-6 shadow-xs space-y-3">
              <h4 className="text-sm font-semibold text-foreground">
                المناطق المغطاة بالخدمة السريعة:
              </h4>
              <ul className="space-y-2.5">
                {coverageAreas.map((area, i) => <li key={area} data-index={i} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                    <span className="size-5 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <Check className="size-3" />
                    </span>
                    <span>{area}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Block 4: FAQ Accordion */}
        <div className="max-w-3xl mx-auto space-y-8">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          ease: "easeOut"
        }} className="text-center space-y-3">
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
              الأسئلة الشائعة حول تنظيف الأفران
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base">
              إجابات مباشرة على استفساراتكم حول آليات العمل، السلامة، وطريقة الحجز.
            </p>
          </motion.div>

          <Accordion type="single" collapsible className="w-full space-y-3">
            {faqs.map((faq, i) => <AccordionItem key={faq.question} data-index={i} value={`item-${i}`} className="border border-border rounded-lg bg-card px-4 shadow-2xs">
                <AccordionTrigger className="text-right text-base font-semibold py-4 hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed text-sm pt-0 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {/* Block 5: Call To Action Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        ease: "easeOut"
      }} className="rounded-2xl bg-secondary text-secondary-foreground p-8 sm:p-12 text-center space-y-6 shadow-md">
          <div className="max-w-2xl mx-auto space-y-3">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
              احجز موعد تنظيف فرنك في حي النخيل الآن
            </h3>
            <p className="text-secondary-foreground/80 text-sm sm:text-base leading-relaxed">
              فريقنا جاهز لتقديم خدمة منزلية فورية ومحترفة بأفضل الأسعار وأعلى جودة. تواصل معنا مباشرة لتحديد الوقت المناسب لك.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6 h-12 shadow-sm">
              <Link to={WHATSAPP_HREF} newTab>
                <MessageCircle className="size-5 ml-2" />
                حجز موعد عبر الواتساب
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="border-secondary-foreground/20 text-secondary-foreground bg-secondary/40 hover:bg-secondary/70 h-12 px-6">
              <a href={PHONE_TEL_HREF}>
                <Phone className="size-5 ml-2" />
                اتصال هاتفي مباشر
              </a>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}